import { useChannelLabel } from '../context/ChannelNumberingContext';
import { waveformLayout, waveformAxis, waveformConfig, waveformClusterColor } from '../utils/waveformStyle';
import React, { useState } from 'react';
import Plot from './ThemedPlot';
import { spikeChannelRows } from '../utils/spikeWaveformCards';

const axisName = (dimension, lane) => `${dimension}${lane === 0 ? '' : lane + 1}`;
const sameRange = (left, right) => left?.[0] === right?.[0] && left?.[1] === right?.[1];

export default function StackedClusterWaveforms({ ids, primaryChannel, clusterWaveforms, referenceAmplitude, highlightedSpike }) {
  const labelChannel = useChannelLabel();
  const [view, setView] = useState({ key: '', ranges: {} });
  const rowsByCluster = ids.map(id => (clusterWaveforms[id] || []).flatMap((waveform, index) =>
    spikeChannelRows(waveform, primaryChannel).map(row => ({ ...row, spikeIndex: waveform.pointIndex ?? waveform.spikeIndex ?? index }))));
  const channels = [...new Set(rowsByCluster.flat().map(row => String(row.channel)))].sort((a, b) => Number(a) - Number(b));
  let amplitude = 0;
  let start = Infinity;
  let end = -Infinity;
  rowsByCluster.flat().forEach(row => {
    row.raw.forEach(value => { amplitude = Math.max(amplitude, Math.abs(value)); });
    row.x.forEach(value => { start = Math.min(start, value); end = Math.max(end, value); });
  });
  const plotHeight = Math.max(300, channels.length * 140 + 62);
  const defaults = { x: Number.isFinite(start) && end > start ? [start, end] : [0, 1],
    y: [-(amplitude || 1) * 1.2, (amplitude || 1) * 1.2] };
  const viewKey = JSON.stringify([primaryChannel, ids, channels, defaults]);
  const ranges = view.key === viewKey ? view.ranges : {};
  const updateRanges = (changes) => setView(previous => {
    const current = previous.key === viewKey ? previous.ranges : {};
    const next = { ...current };
    let changed = false;
    changes.forEach(({ channel, dimension, range }) => {
      const activeRange = current[channel]?.[dimension] || defaults[dimension];
      if (sameRange(activeRange, range)) return;
      next[channel] = { ...next[channel], [dimension]: range };
      changed = true;
    });
    return changed ? { key: viewKey, ranges: next } : previous;
  });
  const handleRelayout = (event) => {
    const changes = [];
    channels.forEach((channel, lane) => ['x', 'y'].forEach(dimension => {
      const axis = `${dimension}axis${lane === 0 ? '' : lane + 1}`;
      const range = event[`${axis}.range`] || [event[`${axis}.range[0]`], event[`${axis}.range[1]`]];
      if (event[`${axis}.autorange`]) changes.push({ channel, dimension, range: defaults[dimension] });
      else if (range.every(Number.isFinite) && range[1] > range[0]) changes.push({ channel, dimension, range });
    }));
    updateRanges(changes);
  };
  return <div className="stacked-waveform-comparison">
    <div className="cluster-waveform-columns stacked-waveform-columns">
    {ids.map((id, index) => {
      const rows = rowsByCluster[index];
      const color = waveformClusterColor(id);
      const means = channels.flatMap((channel, lane) => {
        const channelRows = rows.filter(row => String(row.channel) === channel);
        if (!channelRows.length) return [];
        const x = channelRows[0].x;
        const aligned = channelRows.filter(row => row.x.length === x.length && row.x.every((value, i) => value === x[i]));
        const mean = x.map((_, i) => aligned.reduce((sum, row) => sum + row.raw[i], 0) / aligned.length);
        return [{ type: 'scatter', mode: 'lines', x, y: mean,
          xaxis: axisName('x', lane), yaxis: axisName('y', lane),
          customdata: mean, name: `CH ${labelChannel(channel)} mean`, line: { color, width: 4 }, opacity: 1, showlegend: false,
          hovertemplate: `CH ${labelChannel(channel)} · Mean<br>Time: %{x}<br>Amplitude: %{customdata:.3f}<extra></extra>` }];
      });
      const axes = {};
      // Plotly mutates layout ranges during gestures; keep React's shared ranges separate.
      channels.forEach((channel, lane) => {
        const suffix = lane === 0 ? '' : lane + 1;
        const padding = 3 / (plotHeight - 62);
        axes[`xaxis${suffix}`] = { ...waveformAxis(lane === 0
          ? (clusterWaveforms[id]?.[0]?.timeUnit ? `Time (${clusterWaveforms[id][0].timeUnit})` : 'Time') : ''),
          anchor: axisName('y', lane), domain: [0, 1], range: (ranges[channel]?.x || defaults.x).slice(),
          autorange: false, zeroline: false, showticklabels: lane === 0 };
        axes[`yaxis${suffix}`] = { ...waveformAxis(''), anchor: axisName('x', lane),
          domain: [lane / channels.length + padding, (lane + 1) / channels.length - padding],
          range: (ranges[channel]?.y || defaults.y).slice(), autorange: false,
          showticklabels: false, zeroline: false, showgrid: false };
      });
      return <section key={id} className="stacked-waveform-column" aria-label={`Stacked waveforms for cluster ${id}`}>
        <strong>Cluster {id}</strong>
        {!rows.length ? <p>No waveform samples available for this cluster.</p> : <Plot
          data={[...rows.map(row => {
            const lane = channels.indexOf(String(row.channel));
            const highlighted = String(highlightedSpike?.clusterId) === String(id) && highlightedSpike?.spikeIndex === row.spikeIndex;
            return { type: 'scatter', mode: 'lines', x: row.x, y: row.raw,
              xaxis: axisName('x', lane), yaxis: axisName('y', lane),
              customdata: row.raw, line: { color, width: highlighted ? 3 : 1 },
              opacity: highlighted ? 1 : 0.15, showlegend: false,
              hovertemplate: `CH ${labelChannel(row.channel)} · Spike ${row.spikeIndex}<br>Time: %{x}<br>Amplitude: %{customdata:.3f}<extra></extra>` };
          }), ...means]}
          layout={{ ...waveformLayout(), ...axes, uirevision: viewKey, dragmode: 'zoom',
            annotations: channels.map((channel, lane) => ({ xref: 'paper', x: 0, xanchor: 'right', xshift: -5,
              yref: 'paper', y: (lane + 0.5) / channels.length, text: channel === 'undefined' ? '?' : String(labelChannel(channel)), showarrow: false,
              font: { color: channel === String(primaryChannel) ? '#ef5555' : '#6565ff' } })),
            shapes: channels.flatMap((channel, lane) => [
              { type: 'line', xref: axisName('x', lane), x0: 0, x1: 0,
                yref: `${axisName('y', lane)} domain`, y0: 0, y1: 1, line: { color: '#e87d42', width: 1 } },
              ...(referenceAmplitude == null ? [] : [{ type: 'line', xref: `${axisName('x', lane)} domain`, x0: 0, x1: 1,
                yref: axisName('y', lane), y0: referenceAmplitude, y1: referenceAmplitude, line: { color: '#888888', width: 1.5 } }]),
            ]),
          }} config={waveformConfig} onRelayout={handleRelayout}
          style={{ width: '100%', height: plotHeight }} useResizeHandler />}
      </section>;
    })}
    </div>
  </div>;
}
